// Memory for the workout plan builder, shared by the planner widget and the
// "Back to your plan" bar on exercise pages. Browser only: every function here
// must be called from an effect or an event handler. Nothing is sent anywhere.
//
// - The plan and its answers live in localStorage, so the plan is still there
//   when the visitor comes back another day on the same device. Starting again
//   clears it.
// - Where the visitor was (scroll position, last opened exercise) lives in
//   sessionStorage, because it only matters within one visit.
// - A plan link carries the answers in the URL, so the same plan can be opened
//   on another device or from the PDF.

import type {
    Experience,
    Goal,
    Plan,
    PlannedExercise,
    PlannerInput,
    Setup,
    Sex,
    SoreSpot
} from '@/lib/workout-planner'

import { siteConfig } from '@/constants/site'

export const PLANNER_PATH = '/movement/workout-plan-builder'
export const PLAN_LINK_PARAM = 'plan'
// Which day of the plan an exercise link was opened from (PDF links carry it).
export const PLAN_DAY_PARAM = 'day'

// Where shared links (plan link, QR code, PDF links) point. On the live site,
// the address the visitor is on. In the preview, the public site address,
// because the preview address only opens inside the editor.
export const shareOrigin = (): string =>
    process.env.NODE_ENV === 'production' ? window.location.origin : siteConfig.url.replace(/\/$/, '')

const PLAN_KEY = 'wpf-workout-plan'
const SCROLL_KEY = 'wpf-workout-plan-scroll'
const OPENED_KEY = 'wpf-workout-plan-opened'
// Bump if the Plan or answers shape changes, so old saved plans and links are ignored.
const VERSION = 1

export interface SavedInputs {
    answers: (boolean | null)[]
    age: string
    weight: string
    unit: 'kg' | 'lb'
    heightCm: string
    feet: string
    inches: string
    heightUnit: 'cm' | 'ft'
    sex: Sex
    experience: Experience
    goal: Goal
    days: number
    minutes: number
    setup: Setup
    sore: SoreSpot[]
}

interface SavedPlan {
    version: number
    plan: Plan
    inputs: SavedInputs
}

// Which exercise was last opened from the plan, and from which day.
export interface OpenedFrom {
    day: string
    slug: string
}

type StoreName = 'local' | 'session'

const store = (name: StoreName): Storage | null => {
    try {
        return name === 'local' ? window.localStorage : window.sessionStorage
    } catch {
        return null
    }
}

const read = <T>(name: StoreName, key: string): T | null => {
    try {
        const raw = store(name)?.getItem(key)
        return raw ? (JSON.parse(raw) as T) : null
    } catch {
        return null
    }
}

const write = (name: StoreName, key: string, value: unknown): void => {
    try {
        store(name)?.setItem(key, JSON.stringify(value))
    } catch {
        // Storage full or blocked: the planner still works, it just will not be remembered.
    }
}

const remove = (name: StoreName, key: string): void => {
    try {
        store(name)?.removeItem(key)
    } catch {
        // Nothing to remove.
    }
}

export const readSavedPlan = (): { plan: Plan; inputs: SavedInputs } | null => {
    const saved = read<SavedPlan>('local', PLAN_KEY)
    if (!saved || saved.version !== VERSION || !saved.plan?.days) return null
    return { plan: saved.plan, inputs: saved.inputs }
}

export const savePlan = (plan: Plan, inputs: SavedInputs): void => {
    write('local', PLAN_KEY, { version: VERSION, plan, inputs } satisfies SavedPlan)
}

export const clearSavedPlan = (): void => {
    remove('local', PLAN_KEY)
    remove('session', SCROLL_KEY)
    remove('session', OPENED_KEY)
}

export const saveScroll = (): void => write('session', SCROLL_KEY, Math.round(window.scrollY))

export const readScroll = (): number | null => {
    const value = read<number>('session', SCROLL_KEY)
    return typeof value === 'number' && value > 0 ? value : null
}

export const saveOpenedFrom = (opened: OpenedFrom): void => write('session', OPENED_KEY, opened)

export const readOpenedFrom = (): OpenedFrom | null => read<OpenedFrom>('session', OPENED_KEY)

export interface PlanPosition {
    day: string
    title: string
    index: number
    exercises: PlannedExercise[]
}

// Finds the exercise in the plan, preferring the day it was opened from.
export const findInPlan = (plan: Plan, slug: string, preferredDay: string | null): PlanPosition | null => {
    const days = plan.days.filter((entry) => entry.session && entry.session.exercises.length > 0)
    const ordered = preferredDay
        ? [...days.filter((entry) => entry.day === preferredDay), ...days.filter((entry) => entry.day !== preferredDay)]
        : days

    for (const entry of ordered) {
        const exercises = entry.session!.exercises
        const index = exercises.findIndex((exercise) => exercise.slug === slug)
        if (index >= 0) return { day: entry.day, title: entry.session!.title, index, exercises }
    }

    return null
}

// ---------------------------------------------------------------------------
// Plan links
// ---------------------------------------------------------------------------

// The answers as a short array, in a fixed order, then base64url. Health
// answers only matter as "any yes", so a single flag is kept instead of them.
type LinkPayload = [number, 0 | 1, string, string, string, string, string, string, string, string, string, string, number, number, string, string]

const toBase64Url = (text: string): string => window.btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

const fromBase64Url = (value: string): string => {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/')
    return window.atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
}

export const encodePlanLink = (inputs: SavedInputs): string => {
    const payload: LinkPayload = [
        VERSION,
        inputs.answers.some((answer) => answer === true) ? 1 : 0,
        inputs.age,
        inputs.weight,
        inputs.unit,
        inputs.heightCm,
        inputs.feet,
        inputs.inches,
        inputs.heightUnit,
        inputs.sex,
        inputs.experience,
        inputs.goal,
        inputs.days,
        inputs.minutes,
        inputs.setup,
        inputs.sore.join('+')
    ]
    return toBase64Url(JSON.stringify(payload))
}

const LB_PER_KG = 2.20462
const CM_PER_INCH = 2.54

// The planner's input for a set of answers, so the planner widget and the
// exercise page bar build exactly the same plan from a plan link.
export const plannerInput = (inputs: SavedInputs): PlannerInput => ({
    age: Number(inputs.age),
    weightKg: inputs.unit === 'kg' ? Number(inputs.weight) : Number(inputs.weight) / LB_PER_KG,
    heightCm: inputs.heightUnit === 'cm'
        ? Number(inputs.heightCm)
        : (Number(inputs.feet) * 12 + Number(inputs.inches || 0)) * CM_PER_INCH,
    sex: inputs.sex,
    experience: inputs.experience,
    goal: inputs.goal,
    days: [2, 3, 4, 5].includes(inputs.days) ? inputs.days : 3,
    minutes: [20, 30, 45, 60].includes(inputs.minutes) ? inputs.minutes : 30,
    setup: inputs.setup,
    sore: inputs.sore,
    cautious: inputs.answers.some((answer) => answer === true)
})

// Link to an exercise page that still knows the plan, used in the PDF: opened
// on any device it shows the plan bar and can bring the whole plan back.
export const exerciseLink = (origin: string, exercise: PlannedExercise, day: string, inputs: SavedInputs): string =>
    `${origin}/exercises/${exercise.muscle}/${exercise.slug}?${PLAN_LINK_PARAM}=${encodePlanLink(inputs)}&${PLAN_DAY_PARAM}=${encodeURIComponent(day)}`

const oneOf = <T extends string>(value: unknown, options: readonly T[]): T | null =>
    typeof value === 'string' && (options as readonly string[]).includes(value) ? (value as T) : null

// Returns the answers from a plan link, or null if the link is broken or from
// an older version. Every value is checked against what the planner allows.
// questionCount sizes the health answers; the planner pads them to its own list.
export const decodePlanLink = (value: string, questionCount = 1): SavedInputs | null => {
    try {
        const payload = JSON.parse(fromBase64Url(value)) as unknown
        if (!Array.isArray(payload) || payload.length !== 16 || payload[0] !== VERSION) return null

        const [, cautious, age, weight, unit, heightCm, feet, inches, heightUnit, sex, experience, goal, days, minutes, setup, sore] = payload
        const text = (item: unknown): string => (typeof item === 'string' && /^[0-9.]{0,6}$/.test(item) ? item : '')

        const parsed: SavedInputs = {
            // A link keeps only whether any health answer was yes; the first
            // question carries that, the rest read as no.
            answers: Array.from({ length: questionCount }, (_, index) => (index === 0 ? cautious === 1 : false)),
            age: text(age),
            weight: text(weight),
            unit: oneOf(unit, ['kg', 'lb'] as const) ?? 'kg',
            heightCm: text(heightCm),
            feet: text(feet),
            inches: text(inches),
            heightUnit: oneOf(heightUnit, ['cm', 'ft'] as const) ?? 'cm',
            sex: oneOf(sex, ['female', 'male', 'unspecified'] as const) ?? 'unspecified',
            experience: oneOf(experience, ['new', 'some', 'regular'] as const) ?? 'new',
            goal: oneOf(goal, ['health', 'strength', 'fat-loss', 'endurance'] as const) ?? 'health',
            days: typeof days === 'number' && days >= 1 && days <= 7 ? days : 3,
            minutes: typeof minutes === 'number' && minutes >= 10 && minutes <= 120 ? minutes : 30,
            setup: oneOf(setup, ['none', 'home', 'gym'] as const) ?? 'none',
            sore: typeof sore === 'string' && sore
                ? sore.split('+').filter((item): item is SoreSpot => ['knees', 'back', 'shoulders'].includes(item))
                : []
        }

        return parsed.age && parsed.weight ? parsed : null
    } catch {
        return null
    }
}
